<template>
    <!-- A complete form input for choosing a subset of users.
         Owns its label, its summary row, and the modal it opens, so a caller
         only supplies the users and binds v-model. Picking happens in a modal
         because a long user list would otherwise push the rest of a form out of
         view, and because a plain multi-select is hard to scan.

         Intentionally generic: it does not know what the selection is for.
         Anywhere a subset of users is needed, this is the input. -->
    <div>
        <label  v-if="label"
                class="mb-2 block text-left text-sm font-medium text-gray-700">
            {{ label }}
        </label>

        <p  v-if="description"
            class="m-0 mb-2 text-sm text-gray-500">
            {{ description }}
        </p>

        <div class="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
            <!-- Deliberately a count and not the names: the full list is one
                 click away in the modal, and spelling it out here grows without
                 bound and repeats what the modal already shows. -->
            <div class="min-w-0">
                <p  class="m-0 text-sm"
                    :class="selectedCount > 0 ? 'font-medium text-gray-900' : 'text-gray-500'">
                    {{ summary }}
                </p>
            </div>

            <button type="button"
                    :disabled="disabled || ! hasUsers"
                    class="inline-flex shrink-0 items-center gap-1.5 self-start rounded-md border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium leading-5 text-blue-700 transition-colors duration-150 hover:border-transparent hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-white sm:self-auto"
                    @click="open">
                <tabler-icon    name="users"
                                size-class="h-3.5 w-3.5"
                ></tabler-icon>
                {{ selectedCount > 0 ? changeLabel : chooseLabel }}
            </button>
        </div>

        <p  v-if="! hasUsers"
            class="m-0 mt-2 text-sm text-gray-500">
            {{ emptyMessage }}
        </p>

        <!-- Teleported to the body so a drawer or card that opened this cannot
             clip it, and given a z-index above the drawer's default so it never
             lands behind the thing that opened it. -->
        <teleport   to="body">
            <div    v-if="isOpen"
                    class="fixed inset-0 z-[60] flex items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
            >
                <div    class="absolute inset-0 bg-gray-500 bg-opacity-75"
                        @click="cancel"
                ></div>

                <div class="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-clip rounded-lg bg-white shadow-xl">
                    <div class="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                        <div>
                            <h3 class="m-0 p-0 text-left text-base font-semibold text-gray-900">
                                {{ modalTitle }}
                            </h3>
                            <p  v-if="modalDescription"
                                class="m-0 mt-0.5 text-sm text-gray-500">
                                {{ modalDescription }}
                            </p>
                        </div>

                        <button type="button"
                                class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                @click="cancel">
                            <span class="sr-only">Close</span>
                            <tabler-icon name="x"></tabler-icon>
                        </button>
                    </div>

                    <div class="flex items-center gap-2 border-b border-gray-200 px-5 py-3">
                        <div class="relative flex-1">
                            <tabler-icon    name="search"
                                            size-class="h-4 w-4"
                                            class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            ></tabler-icon>
                            <input  ref="search"
                                    v-model="searchQuery"
                                    type="text"
                                    placeholder="Search users"
                                    class="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            >
                        </div>

                        <!-- One option (Select all, or a single preset when Select all is
                             off) is a plain button; more than one share a Select menu. -->
                        <button v-if="multiple && selectMenuItems.length === 1"
                                type="button"
                                :disabled="selectMenuItems[0].disabled"
                                :class="selectButtonClass"
                                @click="selectMenuItems[0].onSelect()">
                            {{ selectMenuItems[0].buttonLabel }}
                        </button>

                        <action-menu    v-else-if="multiple && selectMenuItems.length > 1"
                                        :items="selectMenuItems"
                                        label="Select"
                        >
                            <template #trigger="{open, toggle}">
                                <button type="button"
                                        :class="[selectButtonClass, 'inline-flex items-center gap-1']"
                                        aria-haspopup="menu"
                                        :aria-expanded="open"
                                        @click="toggle">
                                    Select
                                    <tabler-icon    name="chevronDown"
                                                    size-class="h-4 w-4"
                                    ></tabler-icon>
                                </button>
                            </template>
                        </action-menu>
                    </div>

                    <!-- Holds its row at zero selections too, so ticking the
                         first person does not shift the list under the cursor. -->
                    <div    v-if="multiple"
                            class="flex h-11 shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-5 transition-colors"
                            :class="draftCount > 0 ? 'bg-blue-50' : 'bg-gray-50'">
                        <span   class="text-sm font-medium"
                                :class="draftCount > 0 ? 'text-blue-900' : 'text-gray-500'">
                            {{ draftCount > 0 ? `${draftCount} selected` : 'No users selected' }}
                        </span>

                        <button v-if="draftCount > 0"
                                type="button"
                                class="inline-flex items-center gap-1 text-sm font-medium text-blue-700 transition-colors duration-150 hover:text-blue-900"
                                @click="draft = []">
                            <tabler-icon    name="x"
                                            size-class="h-3.5 w-3.5"
                            ></tabler-icon>
                            Clear
                        </button>
                    </div>

                    <div    v-if="visibleIds.length === 0"
                            class="flex flex-1 items-center justify-center px-5 py-10 text-center">
                        <div>
                            <p class="m-0 text-sm font-medium text-gray-900">No matching users</p>
                            <p class="m-0 mt-0.5 text-sm text-gray-500">Try a different search term.</p>
                        </div>
                    </div>

                    <ul v-else
                        class="m-0 flex-1 list-none overflow-y-auto p-0">
                        <li v-for="id in visibleIds"
                            :key="id">
                            <label class="flex cursor-pointer select-none items-center gap-3 border-b border-gray-100 px-5 py-2.5 transition-colors hover:bg-gray-50">
                                <input  :checked="draft.includes(id)"
                                        :type="multiple ? 'checkbox' : 'radio'"
                                        class="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                        :class="multiple ? 'rounded' : ''"
                                        @change="toggle(id)"
                                >
                                <span class="text-sm text-gray-900">{{ userName(id) }}</span>
                            </label>
                        </li>
                    </ul>

                    <div class="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
                        <button type="button"
                                class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                @click="cancel">
                            Cancel
                        </button>

                        <button type="button"
                                class="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 active:bg-blue-600"
                                @click="confirm">
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </teleport>
    </div>
</template>

<script>
import ActionMenu from "./ActionMenu.vue"
import TablerIcon from "./TablerIcon.vue"

export default {
    name: "UserSelectInput",
    components: {ActionMenu, TablerIcon},
    emits: ['update:modelValue'],
    props: {
        // Selected user ids. Ids arrive as numbers from most APIs but as
        // strings out of a saved JSON blob, so they are compared as strings
        // internally and emitted back in their original shape.
        modelValue: {
            type: Array,
            default: () => ([]),
        },
        /**
         * The users to choose from, in either shape:
         *   {"2": "Tyler Carpenter", "7": "Jane Doe"}
         *   [{id: 2, display_name: "Tyler Carpenter"}, ...]
         *
         * Both are accepted because callers already hold one or the other, and
         * forcing a conversion at every call site is how a shared input stops
         * being worth using.
         */
        users: {
            type: [Object, Array],
            default: () => ({}),
        },
        // Key to read a display name from when `users` is an array of objects.
        labelKey: {
            type: String,
            default: 'display_name',
        },
        idKey: {
            type: String,
            default: 'id',
        },
        label: {
            type: String,
            default: null,
        },
        description: {
            type: String,
            default: null,
        },
        modalTitle: {
            type: String,
            default: 'Select users',
        },
        modalDescription: {
            type: String,
            default: null,
        },
        chooseLabel: {
            type: String,
            default: 'Choose users',
        },
        changeLabel: {
            type: String,
            default: 'Change users',
        },
        // Wording for the zero-selected state, which differs by context: "no
        // recipients" reads very differently from "no approvers".
        emptySummary: {
            type: String,
            default: 'No users selected',
        },
        emptyMessage: {
            type: String,
            default: 'No users are available to select.',
        },
        noun: {
            type: String,
            default: 'user',
        },
        pluralNoun: {
            type: String,
            default: 'users',
        },
        multiple: {
            type: Boolean,
            default: true,
        },
        // Preset groups offered next to "All users" in a Select menu, which then
        // replaces the Select all button: [{label: 'Site Admins', ids: [12, 40]}].
        // An id the picker does not list is skipped, so a preset never counts
        // someone you cannot see.
        quickSelects: {
            type: Array,
            default() {
                return []
            },
        },
        // Off where everyone on the site should never be picked in one click;
        // the presets (if any) remain.
        selectAll: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isOpen: false,
            searchQuery: '',
            // Edited copy, so Cancel genuinely discards.
            draft: [],
        }
    },
    computed: {
        // Normalised to a {id: name} map regardless of which shape came in.
        userMap() {
            if (Array.isArray(this.users)) {
                return this.users.reduce((carry, user) => {
                    carry[String(user[this.idKey])] = user[this.labelKey]

                    return carry
                }, {})
            }

            return this.users ?? {}
        },
        selectButtonClass() {
            return 'shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white'
        },
        selectAllLabel() {
            return this.searchQuery.trim() ? 'Select matching' : 'Select all'
        },
        selectMenuItems() {
            const items = []
            if (this.selectAll) {
                items.push({
                    label: this.searchQuery.trim() ? 'All matching users' : 'All users',
                    buttonLabel: this.selectAllLabel,
                    disabled: this.visibleIds.length === 0 || this.allVisibleSelected,
                    onSelect: () => this.selectAllVisible(),
                })
            }

            return [
                ...items,
                ...this.availableQuickSelects.map((quickSelect) => ({
                    label: quickSelect.label,
                    buttonLabel: `Select ${quickSelect.label}`,
                    disabled: quickSelect.ids.every((id) => this.draft.includes(id)),
                    onSelect: () => this.applyQuickSelect(quickSelect.ids),
                })),
            ]
        },
        availableQuickSelects() {
            if (! this.multiple) {
                return []
            }

            return this.quickSelects
                .map((quickSelect) => ({
                    label: quickSelect.label,
                    ids: (quickSelect.ids ?? []).map((id) => String(id)).filter((id) => id in this.userMap),
                }))
                .filter((quickSelect) => quickSelect.ids.length > 0)
        },
        userIds() {
            return Object.keys(this.userMap)
        },
        hasUsers() {
            return this.userIds.length > 0
        },
        selectedIds() {
            return (this.modelValue ?? []).map((id) => String(id))
        },
        selectedCount() {
            return this.selectedIds.length
        },
        summary() {
            if (this.selectedCount === 0) {
                return this.emptySummary
            }

            const noun = this.selectedCount === 1 ? this.noun : this.pluralNoun

            return `${this.selectedCount} ${noun} selected`
        },
        draftCount() {
            return this.draft.length
        },
        visibleIds() {
            const query = this.searchQuery.trim().toLowerCase()

            if (! query) {
                return this.userIds
            }

            return this.userIds.filter((id) => this.userName(id).toLowerCase().includes(query))
        },
        allVisibleSelected() {
            return this.visibleIds.length > 0
                && this.visibleIds.every((id) => this.draft.includes(id))
        },
    },
    methods: {
        userName(id) {
            return String(this.userMap[id] ?? '')
        },
        open() {
            this.draft = [...this.selectedIds]
            this.searchQuery = ''
            this.isOpen = true

            this.$nextTick(() => this.$refs.search?.focus())
        },
        cancel() {
            this.isOpen = false
        },
        confirm() {
            // Emitted in the shape the ids arrived in. Object keys are always
            // strings, but callers and their backends have always dealt in
            // numeric ids, so a numeric-looking id goes back out as a number
            // rather than silently changing what gets persisted.
            this.$emit('update:modelValue', this.draft.map(
                (id) => (/^\d+$/.test(id) ? Number(id) : id)
            ))

            this.isOpen = false
        },
        toggle(id) {
            if (! this.multiple) {
                this.draft = [id]

                return
            }

            const index = this.draft.indexOf(id)

            if (index === -1) {
                this.draft.push(id)

                return
            }

            this.draft.splice(index, 1)
        },
        selectAllVisible() {
            this.draft = [...new Set([...this.draft, ...this.visibleIds])]
        },
        applyQuickSelect(ids) {
            this.draft = [...new Set([...this.draft, ...ids])]
        },
    },
}
</script>
